import React, {useEffect, useState} from 'react';

import {useMutation} from '@apollo/client/react';
import {X, Building2, AlertCircle} from 'lucide-react';
import {gql} from 'types/__generated__';

const CREATE_CUSTOMER_ORGANISATION = gql(`
  mutation CreateCustomerOrganisation($input: CreateCustomerOrganisationInput!) {
    createCustomerOrganisation(input: $input)
  }
`);

interface CreateOrganisationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (organisationId: string) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CreateOrganisationModal({isOpen, onClose, onSuccess}: CreateOrganisationModalProps) {
  const [organisationName, setOrganisationName] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');

  // Reset form every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setOrganisationName('');
      setEmail('');
      setFirstName('');
      setLastName('');
      setError('');
    }
  }, [isOpen]);

  const [createOrganisation, {loading}] = useMutation(CREATE_CUSTOMER_ORGANISATION, {
    refetchQueries: ['Organisations', 'DashboardOrganisations'],
    awaitRefetchQueries: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!organisationName.trim()) {
      setError('Organisation name is required');
      return;
    }
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError('A valid owner email address is required');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError("The owner's first and last name are required");
      return;
    }

    try {
      const result = await createOrganisation({
        variables: {
          input: {
            organisationName: organisationName.trim(),
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          },
        },
      });

      const organisationId = (result.data as {createCustomerOrganisation: string} | null | undefined)?.createCustomerOrganisation;
      if (!organisationId) {
        setError('The organisation was not created — the server returned no id');
        return;
      }

      onSuccess(organisationId);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create organisation');
      console.error('Failed to create organisation:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Building2 size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">New Organisation</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Create an organisation on behalf of a new customer</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="flex items-center space-x-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Organisation Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organisation Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={organisationName}
                onChange={(e) => setOrganisationName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter organisation name"
                autoFocus
                required
              />
            </div>

            {/* Owner */}
            <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 pt-4">Account owner</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  The first user of the organisation. They are created together with the organisation as an Admin and become its account owner
                  (billing and seat purchasing).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="owner@example.com"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Organisation'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
